import React, {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styles from './Table.module.scss';
import {
    CheckBox,
    Flex,
    Icon,
    Link,
    PriorityIcon,
    Scrollbar,
    Text
} from '@priority-software/priority-style-react';
import {isRTL} from '../../../../Localization';
import {useScrollWrapDimensions} from '../../../../hooks/customScroll/useScrollWrapDimensions';
import {useHover} from '../../../../hooks/useHover';
import {useDNDContext} from './DNDController/useDNDContext';
import {createPortal} from 'react-dom';
import {DNDState} from './DNDController/DNDContext';
import _ from 'lodash';
import {Dictionary} from '../../../EOYWizard/types'; // TODO: Eugenia consider to move this to some common place
import {addSort} from '../../../Form/actions/filters';
import cx from 'classnames';
import {apiLocker, Column, Form, Record} from '../../../../stores/Form';
import {ISort} from '../../../../stores/Form/Sort';
import {HeaderCell} from '../HeaderCell';
import {Point} from './Point';
import {priority} from '@priority-software/priority-proto-api/lib/nform';
import {getCurProcDname, startEntity} from '../../../../_shared/GWTBridge/invoke/genPage';
import {ActivationSource} from '../../../../constants/ActivationSource';
import {texts} from '../../texts';
import {useWidthContext} from './WidthController/useWidthContext';
import {getZoomFormNameFromServer} from '../../../Form/actions/zoom';
import {
    DEFAULT_COLUMN_WIDTH,
    DND_PLACEHOLDER_HEIGHT,
    MAX_COLUMN_WIDTH,
    MIN_COLUMN_WIDTH
} from '../../constants';
import {useScrollWrapper} from '../RoutesWrapper';

export interface TableProps {
    groupColor?: string;
    height?: number;
    contentClassName?: string;
    columns: Column[];
    records: Record[];
    pinnedColumns: number[];
    id: string;
    form?: Form;
    handleGrouping?: boolean;
    onGroupOpen?: (groupIndex: number) => void;
    getGroupInnerCount?: (groupIndex: number) => number;
    renderCell: (columnIndex: number, recordIndex: number) => React.ReactNode | string;
    getCellValue: (columnIndex: number, recordIndex: number) => string;
    renderPoint?: (record: Record, records: Record[], index?: number) => React.ReactNode | string;
    renderRowActions?: (recordIndex: number) => React.ReactNode;
    dndState?: DNDState;
    groupBy?: number;
    groupRenderCell?: (columnIndex: number, groupByValue: string) => React.ReactNode;
    onSelectRows?: (rows: number[], tableId: string) => void;
    showFilter?: boolean;
    setIsScreenLocked?: (isLocked: boolean) => void;
    emptyState?: React.ReactNode;
    selectedRows?: Record[];
    isGroupLoading?: boolean;
    isFullyReloaded?: boolean;
}

export const INITIAL_COLUMN_WIDTH = 110;
let lastClickedRowIndex: number;

export const Table: FC<TableProps> = props => {
    const {
        groupColor,
        dndState,
        form,
        onSelectRows,
        id,
        contentClassName,
        height,
        renderPoint,
        showFilter = false,
        setIsScreenLocked,
        handleGrouping = true,
        onGroupOpen,
        selectedRows = [],
        getGroupInnerCount,
        isGroupLoading,
        isFullyReloaded
    } = props;

    const isRtl = isRTL();

    const innerWrapperRef = useRef<HTMLDivElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const scrollbarRef = useRef();
    useScrollWrapper(scrollbarRef);

    const {setWrapRef, maxHeight: scrollMaxHeight} = useScrollWrapDimensions();
    const {setRef, isHovering} = useHover();

    const columns = useMemo(
        () => props.columns.filter(x => !props.pinnedColumns.includes(x.ID as number)),
        [props.columns, props.pinnedColumns]
    );

    const pinnedColumns = useMemo(
        () => props.columns.filter(x => props.pinnedColumns.includes(x.ID as number)),
        [props.columns, props.pinnedColumns]
    );

    const records = useMemo(() => {
        if (props.groupBy === undefined || !handleGrouping) {
            return props.records;
        } else {
            const records = _.sortBy(props.records, [
                o => props.getCellValue(props.groupBy, o.recordIndex) as string
            ]);
            const result: Record[] = [];
            let counter = 1;
            for (let i = 0; i < records.length; i++) {
                if (
                    result.length === 0 ||
                    (props.getCellValue(props.groupBy, records[i - 1].recordIndex) as string) !==
                        (props.getCellValue(props.groupBy, records[i].recordIndex) as string)
                ) {
                    const groupRecord = new Record(
                        records[i].parentForm,
                        records[i].properties,
                        -counter
                    );
                    result.push(groupRecord);
                    counter++;
                }
                result.push(records[i]);
            }
            return result;
        }
    }, [props.records, props.groupBy, handleGrouping]);

    const [resizeColumnId, setResizeColumnId] = useState<number | undefined>(undefined);
    const [initialResizeX, setInitialResizeX] = useState<number | undefined>(undefined);
    const [initialWidth, setInitialWidth] = useState<number | undefined>(undefined);
    const [currentWidth, setCurrentWidth] = useState<number | undefined>(undefined);
    const [isResizeFinished, setIsResizeFinished] = useState<boolean>(false);
    const [groupOpen, setGroupOpen] = useState<Dictionary<boolean>>({});

    useEffect(() => {
        setGroupOpen({});
    }, [isFullyReloaded]);

    const [hoveredRow, setHoveredRow] = useState<number | undefined>(undefined);

    const [checkedRows, setCheckedRows] = useState<number[]>([]);

    const handleRowsSelect = (newSelectedRows: number[]) => {
        onSelectRows?.(newSelectedRows, id);
    };

    const selectedRowsIndexes = useMemo(
        () => selectedRows.map(x => x.recordIndex),
        [selectedRows.map(x => x.recordIndex)]
    );

    useEffect(() => {
        if (!_.isEqual(selectedRowsIndexes, checkedRows)) {
            setCheckedRows(selectedRowsIndexes);
        }
    }, [selectedRowsIndexes]);

    const columnStyleFunc = useCallback(
        (column: Column) => {
            const columns = props.records?.[0]?.parentForm?.columns;
            const recordColumn = columns?.find(c => c.ID === column.ID);
            const columnWidth = recordColumn?.columnWidth || DEFAULT_COLUMN_WIDTH;
            return {
                width: `${resizeColumnId === column.ID ? currentWidth : columnWidth}px`
            };
        },
        [props.records, resizeColumnId, currentWidth]
    );

    const rowToGroupId = useCallback(
        (rowId: number) =>
            rowId < 0
                ? (Math.abs(rowId) - 1).toString()
                : (props.getCellValue(props.groupBy, rowId) as string),
        [records]
    );

    useEffect(() => {
        if (dndState && !dndState.isMoving && dndState.coordinates) {
            const wrapperRect = wrapperRef.current.getBoundingClientRect();
            if (
                wrapperRect.left <= dndState.coordinates[0] &&
                wrapperRect.top <= dndState.coordinates[1] &&
                wrapperRect.bottom >= dndState.coordinates[1] &&
                wrapperRect.right >= dndState.coordinates[0]
            ) {
                dndState.setTo({
                    id: props.id,
                    group: props.groupBy ? rowToGroupId(hoveredRow) : undefined
                    // row: records.find(r => r.recordIndex === hoveredRow)
                });

                setHoveredRow(undefined);
            }
        }
    }, [dndState?.isMoving, dndState?.coordinates]);

    useEffect(() => {
        if (resizeColumnId) {
            const mouseUpHandler = () => {
                if (resizeColumnId !== undefined && initialResizeX !== undefined) {
                    setIsResizeFinished(true);
                }
            };

            const mouseMoveHandler = (event: MouseEvent) => {
                if (resizeColumnId !== undefined && initialResizeX !== undefined) {
                    const diff = isRtl
                        ? initialResizeX - event.screenX
                        : event.screenX - initialResizeX;
                    const newValue = initialWidth + diff;
                    if (newValue > MIN_COLUMN_WIDTH && newValue < MAX_COLUMN_WIDTH) {
                        setCurrentWidth(newValue);
                    } else if (newValue >= MAX_COLUMN_WIDTH) {
                        setCurrentWidth(MAX_COLUMN_WIDTH);
                    } else if (newValue <= MIN_COLUMN_WIDTH) {
                        setCurrentWidth(MIN_COLUMN_WIDTH);
                    }
                }
            };

            document.body.addEventListener('mouseup', mouseUpHandler);
            wrapperRef.current.addEventListener('mousemove', mouseMoveHandler);

            return () => {
                document.body.removeEventListener('mouseup', mouseUpHandler);
                wrapperRef.current.removeEventListener('mousemove', mouseMoveHandler);
            };
        }
        return () => {};
    }, [resizeColumnId]);

    const {setWidth, setFullWidth, width} = useWidthContext();

    useEffect(() => {
        if (_.isEqual(width, {})) {
            setFullWidth(
                [...columns, ...pinnedColumns]
                    .map(column => ({id: column.ID.toString(), width: column.width}))
                    .reduce((prev, curr) => ({...prev, [curr.id]: curr.width}), {})
            );
        }
    }, [columns, pinnedColumns]);

    useEffect(() => {
        if (isResizeFinished) {
            setIsResizeFinished(false);
            setWidth(resizeColumnId.toString(), currentWidth);
            setResizeColumnId(undefined);
            setInitialResizeX(undefined);
            setInitialWidth(undefined);
        }
    }, [isResizeFinished]);

    useEffect(() => {
        if (dndState && dndState.isMoving) {
            const mouseUpHandler = () => {
                dndState.setIsMoving(false);
            };

            const mouseMoveHandler = (event: MouseEvent) => {
                dndState.setCoordinates([event.pageX, event.pageY]);
            };

            document.body.addEventListener('mouseup', mouseUpHandler);
            document.body.addEventListener('mousemove', mouseMoveHandler);

            return () => {
                document.body.removeEventListener('mouseup', mouseUpHandler);
                document.body.removeEventListener('mousemove', mouseMoveHandler);
            };
        }
        return () => {};
    }, [dndState?.isMoving]);

    const handleSort = useCallback(
        async (e, id: number, sortedColumn: ISort) => {
            if (resizeColumnId !== undefined) {
                return;
            }
            e.stopPropagation();
            if (form) {
                if (apiLocker.isLock(form.session)) return;
                const sortData = {
                    ColumnID: id,
                    Desc: sortedColumn ? !sortedColumn.Desc : false
                };
                form.resetSort();
                await addSort(form, sortData);
            }
        },
        [form, resizeColumnId]
    );

    const handleAllSelect = useCallback(() => {
        if (selectedRows.length === records.length) {
            handleRowsSelect([]);
        } else {
            handleRowsSelect(records?.map(x => x.recordIndex));
        }
    }, [selectedRows, records]);

    const onChangeCheckbox = useCallback(
        (e: MouseEvent, record: Record, groupRecords: Record[]) => {
            let newCheckedRows = [];
            if (record.recordIndex >= 0) {
                let newCheckedRows = [];
                //in this line we check if we have selected rows or last clicked row
                if (checkedRows.length && lastClickedRowIndex !== undefined && e.shiftKey) {
                    const recordIndexInArray = records.findIndex(
                        rec => rec.recordIndex === record.recordIndex
                    );
                    const lastClickedRowIndexInArray = records.findIndex(
                        rec => rec.recordIndex === lastClickedRowIndex
                    );
                    newCheckedRows = [
                        ...records
                            .map((rec, i) =>
                                lastClickedRowIndexInArray > recordIndexInArray
                                    ? i <= lastClickedRowIndexInArray && i >= recordIndexInArray
                                        ? rec.recordIndex
                                        : null
                                    : i >= lastClickedRowIndexInArray && i <= recordIndexInArray
                                    ? rec.recordIndex
                                    : null
                            )
                            .filter(x => x !== null),
                        ...checkedRows
                    ];
                    handleRowsSelect([...new Set(newCheckedRows)]);
                    return;
                }
                const parentRow = findParentRow(
                    records,
                    records.findIndex(rec => rec.recordIndex === record.recordIndex)
                );
                if (
                    checkedRows.includes(record.recordIndex) ||
                    (parentRow && checkedRows.includes(parentRow.recordIndex))
                ) {
                    newCheckedRows = checkedRows.filter(
                        x => x !== record.recordIndex && (!parentRow || x != parentRow.recordIndex)
                    );
                    //special treatment for the case when the items were checked automatically on selected location open
                    if (parentRow && checkedRows.includes(parentRow.recordIndex)) {
                        for (
                            let i =
                                records.findIndex(r => r.recordIndex === parentRow.recordIndex) + 1;
                            i < records.length;
                            i++
                        ) {
                            if (records[i].recordIndex < 0) {
                                break;
                            }
                            if (
                                records[i].recordIndex !== record.recordIndex &&
                                !checkedRows.includes(records[i].recordIndex)
                            ) {
                                newCheckedRows.push(records[i].recordIndex);
                            }
                        }
                    }
                } else {
                    let isAllGroupChecked = false;
                    if (parentRow) {
                        for (
                            let i =
                                records.findIndex(r => r.recordIndex === parentRow.recordIndex) + 1;
                            i < records.length;
                            i++
                        ) {
                            if (records[i].recordIndex < 0) {
                                break;
                            }
                            if (
                                records[i].recordIndex !== record.recordIndex &&
                                !checkedRows.includes(records[i].recordIndex)
                            ) {
                                isAllGroupChecked = false;
                                break;
                            }
                            isAllGroupChecked = true;
                        }
                    }
                    newCheckedRows = [
                        ...checkedRows,
                        record.recordIndex,
                        isAllGroupChecked ? parentRow.recordIndex : null
                    ];
                }
                lastClickedRowIndex = record.recordIndex;
                handleRowsSelect(newCheckedRows);
            } else {
                const set = new Set(checkedRows);
                const isChecked = checkedRows.includes(record.recordIndex);
                for (const record of groupRecords) {
                    if (isChecked) {
                        set.delete(record.recordIndex);
                    } else {
                        set.add(record.recordIndex);
                    }
                }
                if (checkedRows.includes(record.recordIndex)) {
                    set.delete(record.recordIndex);
                } else {
                    set.add(record.recordIndex);
                }
                newCheckedRows = Array.from(set);
                let addNewCheckedRows = [];
                if (checkedRows.length && lastClickedRowIndex !== undefined && e.shiftKey) {
                    const recordIndexInArray = records.findIndex(
                        rec => rec.recordIndex === record.recordIndex
                    );
                    const lastClickedRowIndexInArray = records.findIndex(
                        rec => rec.recordIndex === lastClickedRowIndex
                    );
                    addNewCheckedRows = records
                        .map((rec, i) =>
                            lastClickedRowIndexInArray > recordIndexInArray
                                ? i <= lastClickedRowIndexInArray && i >= recordIndexInArray
                                    ? rec.recordIndex
                                    : null
                                : i >= lastClickedRowIndexInArray && i <= recordIndexInArray
                                ? rec.recordIndex
                                : null
                        )
                        .filter(x => x !== null);
                }
                newCheckedRows = [...newCheckedRows, ...addNewCheckedRows];
                lastClickedRowIndex = record.recordIndex;
                handleRowsSelect(newCheckedRows);
            }
        },
        [onSelectRows, checkedRows, id, records]
    );

    const openForm = async (event: React.MouseEvent, column: Column, record: Record) => {
        event.preventDefault();
        event.stopPropagation();
        setIsScreenLocked(true);
        const parentForm = record.parentForm;
        const currRow = parentForm.records.findIndex(rec => rec.recordIndex === record.recordIndex);
        parentForm.setCurrentRow(currRow);
        const zoomFormName = await getZoomFormNameFromServer(parentForm, column.ID as number);
        startEntity(
            zoomFormName,
            column.Name,
            record.getField(column.ID as number).origValue.toString(),
            null,
            ActivationSource.ADP,
            getCurProcDname()
        );
    };

    const findParentRow = (records: Record[], startIndex): Record => {
        for (let j = startIndex - 1; j >= 0; j--) {
            if (records[j].recordIndex < 0) return records[j]; // Return the first negative number found before num
        }

        return null;
    };

    const tableContent = useMemo(() => {
        return (
            <>
                <div className={cx(styles.content, contentClassName)}>
                    {records.map((record, i) => {
                        const groupRecords: Record[] = [];
                        let parentRecord;
                        if (record.recordIndex < 0) {
                            for (let j = i + 1; j < records.length; ++j) {
                                if (records[j].recordIndex < 0) {
                                    break;
                                } else {
                                    groupRecords.push(records[j]);
                                }
                            }
                        } else {
                            parentRecord = findParentRow(records, i);
                        }

                        const renderCell = (column: Column) => {
                            const columnStyle = columnStyleFunc(column);

                            return (
                                <div
                                    key={column.ID as number}
                                    style={columnStyle}
                                    className={styles.cell}
                                    onMouseDown={event => {
                                        dndState?.setFrom({
                                            id: props.id,
                                            group:
                                                record.recordIndex < 0
                                                    ? rowToGroupId(record.recordIndex)
                                                    : undefined
                                        });
                                        dndState?.setTo(undefined);
                                        let newCheckedRows = [];
                                        if (record.recordIndex < 0) {
                                            const set = new Set(checkedRows);
                                            set.add(record.recordIndex);
                                            for (const record of groupRecords) {
                                                set.add(record.recordIndex);
                                            }
                                            newCheckedRows = Array.from(set);
                                        } else {
                                            newCheckedRows = checkedRows.includes(
                                                record.recordIndex
                                            )
                                                ? checkedRows
                                                : [...checkedRows, record.recordIndex];
                                        }
                                        dndState?.setMovingRecords(
                                            newCheckedRows.map(i =>
                                                records.find(r => r.recordIndex === i)
                                            )
                                        );
                                        dndState?.setCoordinates([event.pageX, event.pageY]);
                                        dndState?.setIsMoving(true);
                                    }}
                                    onMouseUp={() => setHoveredRow(record.recordIndex)}
                                >
                                    <Flex
                                        componentClasses={styles.groupCellContent}
                                        alignItems={'center'}
                                        justifyContent={'center'}
                                    >
                                        {record.recordIndex >= 0 &&
                                        column.Zoom === priority.netitems.form.ZoomType.ZoomZoom &&
                                        record.parentForm.level === 0 ? (
                                            <Link
                                                ellipsis
                                                onMouseDown={event =>
                                                    openForm(event, column, record)
                                                }
                                            >
                                                {props.renderCell(
                                                    column.ID as number,
                                                    record.recordIndex
                                                )}
                                            </Link>
                                        ) : (
                                            <Text componentClasses={styles.text}>
                                                {record.recordIndex >= 0
                                                    ? props.renderCell(
                                                          column.ID as number,
                                                          record.recordIndex
                                                      )
                                                    : props.groupRenderCell?.(
                                                          column.ID as number,
                                                          (
                                                              Math.abs(records[i].recordIndex) - 1
                                                          ).toString()
                                                      )}
                                            </Text>
                                        )}
                                    </Flex>
                                </div>
                            );
                        };

                        const renderPinnedContent = record => (
                            <div className={styles.pinnedTableWrapper}>
                                <div className={styles.pinnedTable}>
                                    <div className={styles.pinnedTableRow}>
                                        {props.groupBy !== undefined && (
                                            <div className={cx(styles.cell, styles.groupCell)}>
                                                <Flex
                                                    componentClasses={styles.groupCellContent}
                                                    alignItems={'center'}
                                                    justifyContent={'center'}
                                                    onClick={() => {
                                                        if (!isGroupLoading) {
                                                            setGroupOpen(groupOpen => ({
                                                                ...groupOpen,
                                                                [record.recordIndex]:
                                                                    !groupOpen[record.recordIndex]
                                                            }));
                                                            if (!groupOpen[record.recordIndex]) {
                                                                onGroupOpen?.(record.recordIndex);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    {record.recordIndex < 0 ? (
                                                        <Icon
                                                            icon={
                                                                !groupOpen[record.recordIndex]
                                                                    ? isRtl
                                                                        ? PriorityIcon.arrowLeft
                                                                        : PriorityIcon.arrowRight
                                                                    : PriorityIcon.arrowDown
                                                            }
                                                            disabled={isGroupLoading}
                                                        />
                                                    ) : (
                                                        <div
                                                            className={styles.groupingDash}
                                                            style={{borderColor: groupColor}}
                                                        ></div>
                                                    )}
                                                </Flex>
                                            </div>
                                        )}
                                        <div
                                            className={cx(styles.cell, styles.checkboxCell, {
                                                [styles.pinned]:
                                                    pinnedColumns.length === 0 &&
                                                    props.groupBy === undefined
                                            })}
                                        >
                                            <Flex componentClasses={cx(styles.checkboxCellContent)}>
                                                <CheckBox
                                                    componentClasses={styles.checkbox}
                                                    checked={
                                                        record.recordIndex >= 0
                                                            ? checkedRows.includes(
                                                                  record.recordIndex
                                                              ) ||
                                                              (parentRecord &&
                                                                  checkedRows.includes(
                                                                      parentRecord.recordIndex
                                                                  ))
                                                            : checkedRows.includes(
                                                                  record.recordIndex
                                                              )
                                                    }
                                                    onChange={e => {
                                                        onChangeCheckbox(e, record, groupRecords);
                                                    }}
                                                />
                                            </Flex>
                                        </div>
                                        {renderPoint && (
                                            <div className={styles.checkboxColumn}>
                                                {renderPoint(record, records, i)}
                                            </div>
                                        )}
                                        {pinnedColumns.map(renderCell)}
                                    </div>
                                </div>
                            </div>
                        );

                        const findTopGroup = (index: number): Record => {
                            if (records[index].recordIndex >= 0) {
                                return findTopGroup(index - 1);
                            } else {
                                return records[index];
                            }
                        };

                        if (record.recordIndex >= 0 && props.groupBy !== undefined) {
                            const group = findTopGroup(i);

                            if (!groupOpen[group.recordIndex]) {
                                return null;
                            }
                        }
                        return (
                            <div key={record.recordIndex} className={styles.row}>
                                <div className={styles.pinned}>{renderPinnedContent(record)}</div>
                                {columns.map(renderCell)}
                            </div>
                        );
                    })}
                </div>
            </>
        );
    }, [
        contentClassName,
        checkedRows,
        dndState?.isMoving,
        columnStyleFunc,
        columns,
        pinnedColumns,
        groupOpen,
        props.renderRowActions,
        props.groupBy,
        groupColor,
        isRtl,
        isGroupLoading,
        records
    ]);

    const renderHeader = useCallback(() => {
        return (
            <div className={styles.headerRow} onMouseUp={() => setHoveredRow(undefined)}>
                <div className={cx(styles.pinned, styles.pinnedHeader)}>
                    <div className={styles.pinnedTableWrapper}>
                        <div className={styles.pinnedTable}>
                            <div className={styles.pinnedTableRow}>
                                {props.groupBy !== undefined && (
                                    <div className={styles.groupColumn}></div>
                                )}
                                <div className={cx(styles.cell, styles.checkboxCell)}>
                                    <Flex componentClasses={cx(styles.checkboxCellContent)}>
                                        <CheckBox
                                            componentClasses={styles.checkbox}
                                            checked={
                                                records.find(x => x.recordIndex < 0)
                                                    ? selectedRows.filter(x => x.recordIndex < 0)
                                                          .length ===
                                                      records.filter(x => x.recordIndex < 0).length
                                                    : selectedRows.length === records.length
                                            }
                                            onChange={handleAllSelect}
                                        />
                                    </Flex>
                                </div>
                                <div className={styles.checkboxColumn}>
                                    <Point
                                        color={groupColor}
                                        emptyType
                                        className={styles.headerPoint}
                                    />
                                </div>
                                {pinnedColumns.map((column, index) => (
                                    <HeaderCell
                                        key={column.ID as number}
                                        isDisabledResize={index === columns.length}
                                        column={column}
                                        currentWidth={currentWidth}
                                        resizeColumnId={resizeColumnId}
                                        form={form}
                                        setInitialWidth={setInitialWidth}
                                        setCurrentWidth={setCurrentWidth}
                                        setResizeColumnId={setResizeColumnId}
                                        handleSort={handleSort}
                                        setInitialResizeX={setInitialResizeX}
                                        showFilter={showFilter}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                {columns.map((column, index) => (
                    <HeaderCell
                        key={column.ID as number}
                        isDisabledResize={index === columns.length}
                        column={column}
                        currentWidth={currentWidth}
                        resizeColumnId={resizeColumnId}
                        form={form}
                        setInitialResizeX={setInitialResizeX}
                        setInitialWidth={setInitialWidth}
                        setCurrentWidth={setCurrentWidth}
                        setResizeColumnId={setResizeColumnId}
                        handleSort={handleSort}
                        showFilter={showFilter}
                    />
                ))}
            </div>
        );
    }, [isRtl, columnStyleFunc, columns, pinnedColumns, props.groupBy]);

    useEffect(() => {
        if (isRtl && scrollbarRef?.current) {
            (scrollbarRef.current as any).scrollToRight();
        }
    }, [props.columns]);

    const countOfDraggingItems: number | undefined = useMemo(() => {
        if (dndState.from && dndState.isMoving && dndState.from.id === props.id) {
            if (dndState?.movingRecords.length || 0 > 0) {
                let count = 0;
                const groupRecords = dndState.movingRecords.filter(x => x.recordIndex < 0);
                const singleRecords = dndState.movingRecords.filter(x => x.recordIndex >= 0);

                for (const groupObj of groupRecords) {
                    count += getGroupInnerCount(-groupObj.recordIndex - 1);
                }

                for (const singleObj of singleRecords) {
                    if (groupRecords.length > 0) {
                        let groupIndex = records.findIndex(
                            x => x.recordIndex === singleObj.recordIndex
                        );
                        while (records[groupIndex].recordIndex >= 0) {
                            groupIndex--;
                        }
                        if (
                            !groupRecords
                                .map(x => x.recordIndex)
                                .includes(records[groupIndex].recordIndex)
                        ) {
                            count++;
                        }
                    } else {
                        count++;
                    }
                }

                return count;
            } else {
                return selectedRows.filter(x => x.recordIndex >= 0).length;
            }
        }
        return undefined;
    }, [
        selectedRows,
        dndState?.from,
        dndState?.isMoving,
        dndState?.movingRecords,
        getGroupInnerCount
    ]);

    return (
        <div
            className={cx(styles.container, {
                [styles.disabledHover]: dndState.isMoving,
                [styles.rtl]: isRtl
            })}
            style={{height}}
            ref={value => {
                setWrapRef(value);
                wrapperRef.current = value;
            }}
        >
            {records.length === 0 && props.emptyState ? (
                props.emptyState
            ) : (
                <div ref={setRef}>
                    <Scrollbar
                        autoHide={!isHovering}
                        autoHeight
                        autoHeightMax={scrollMaxHeight}
                        autoHeightMin={scrollMaxHeight}
                        rtl={isRtl}
                        scrollbarRef={scrollbarRef}
                    >
                        <div className={styles.scrollWrapperOuter}>
                            <div className={styles.scrollWrapperInner}>
                                <div className={styles.wrapper}>
                                    <Flex
                                        ref={innerWrapperRef}
                                        className={styles.innerWrapper}
                                        justifyContent={'flex-start'}
                                        alignItems={'flex-start'}
                                        direction={'row'}
                                    >
                                        <div className={styles.table}>
                                            <div className={styles.header}>{renderHeader()}</div>
                                            {tableContent}
                                            {dndState &&
                                                dndState.isMoving &&
                                                dndState.from?.id === props.id &&
                                                dndState.coordinates !== undefined &&
                                                (createPortal(
                                                    <div
                                                        className={styles.moving}
                                                        style={{
                                                            left: dndState.coordinates[0] + 5,
                                                            top:
                                                                dndState.coordinates[1] -
                                                                document.body.offsetTop -
                                                                DND_PLACEHOLDER_HEIGHT / 2
                                                        }}
                                                    >
                                                        <div className={styles.dragContainer}>
                                                            <Text
                                                                skin={'paragraph-2'}
                                                                textColor={'primary-500'}
                                                            >
                                                                {texts['drag_x_items'].replace(
                                                                    'X',
                                                                    countOfDraggingItems
                                                                )}
                                                            </Text>
                                                        </div>
                                                    </div>,
                                                    document.body
                                                ) as React.ReactNode)}
                                        </div>
                                    </Flex>
                                </div>
                            </div>
                        </div>
                    </Scrollbar>
                </div>
            )}
        </div>
    );
};

export const TableWithDnd: FC<Omit<TableProps, 'dndState'>> = props => {
    const dndState = useDNDContext();
    return <Table {...props} dndState={dndState} />;
};
